import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Badge, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UserService } from '../UserService';
import { IAdvancedDelivery, IAdvancedDeliveryAddress } from '../../../model/user/IAdvancedDelivery';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import ToggleChip from '../../../shared/components/ToggleChip';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';
import { ErrorToast } from '../../../utils/toasters';

interface IUserAdvancedDeliveryTab {
    userId: number;
    isActive: boolean;
    onDirtyChange: (isDirty: boolean) => void;
}

export interface IUserAdvancedDeliveryTabHandle {
    /** Shows the field errors and returns whether the draft can be saved. */
    validate: () => boolean;
    save: () => Promise<void>;
}

type Draft = IAdvancedDeliveryAddress & { useOwnAddress: boolean };

const EMPTY_ADDRESS: IAdvancedDeliveryAddress = {
    firstName: null,
    lastName: null,
    company: null,
    street: null,
    streetNumber: null,
    addressLine2: null,
    zip: null,
    city: null,
    state: null,
    country: null,
    phoneNumber: null,
    deliveryInstructions: null,
};

const REQUIRED_FIELDS: [keyof IAdvancedDeliveryAddress, string][] = [
    ['firstName', 'First name is required'],
    ['lastName', 'Last name is required'],
    ['street', 'Street is required'],
    ['streetNumber', 'Street number is required'],
    ['zip', 'Postal code is required'],
    ['city', 'City is required'],
    ['country', 'Country is required'],
    ['phoneNumber', 'Phone number is required'],
];

const toDraft = (data: IAdvancedDelivery): Draft => ({
    ...EMPTY_ADDRESS,
    ...data.delivery,
    useOwnAddress: data.useOwnAddress,
});

const sectionTitle = (title: string, badge?: React.ReactNode) => (
    <div className="d-flex align-items-center gap-3 mb-4">
        <h5 className="m-0">{title}</h5>
        {badge}
    </div>
);

const usedBadge = (
    <Badge bg="" className="bg-success-subtle text-success fs-8">
        Used for shipping
    </Badge>
);

const readOnlyRow = (label: string, value?: string | null) => (
    <div className="d-flex justify-content-between gap-4 py-2 border-bottom">
        <span className="text-muted">{label}</span>
        <span className="text-end fw-semibold text-break">{value || '-'}</span>
    </div>
);

const ReadOnlyAddress = ({ address }: { address: IAdvancedDeliveryAddress | null }) => {
    if (!address) {
        return <div className="text-muted">No delivery address set.</div>;
    }
    return (
        <Row>
            <Col lg={6}>
                {readOnlyRow('First name', address.firstName)}
                {readOnlyRow('Last name', address.lastName)}
                {readOnlyRow('Company', address.company)}
                {readOnlyRow('Street', address.street)}
                {readOnlyRow('Street number', address.streetNumber)}
                {readOnlyRow('Address line 2', address.addressLine2)}
            </Col>
            <Col lg={6}>
                {readOnlyRow('Postal code', address.zip)}
                {readOnlyRow('City', address.city)}
                {readOnlyRow('State', address.state)}
                {readOnlyRow('Country', address.country ? getCountryName(address.country) : null)}
                {readOnlyRow('Phone', address.phoneNumber)}
                {readOnlyRow('Delivery instructions', address.deliveryInstructions)}
            </Col>
        </Row>
    );
};

export const UserAdvancedDeliveryTab = forwardRef<IUserAdvancedDeliveryTabHandle, IUserAdvancedDeliveryTab>(
    function UserAdvancedDeliveryTab({ userId, isActive, onDirtyChange }, ref) {
        const { country } = useSelector((state: any) => state.enums);
        const [data, setData] = useState<IAdvancedDelivery | null>(null);
        const [draft, setDraft] = useState<Draft | null>(null);
        const [initialSnapshot, setInitialSnapshot] = useState('');
        const [isLoading, setIsLoading] = useState(false);
        const [showErrors, setShowErrors] = useState(false);

        const isDirty = draft !== null && JSON.stringify(draft) !== initialSnapshot;

        useEffect(() => {
            onDirtyChange(isDirty);
        }, [isDirty, onDirtyChange]);

        const applyData = useCallback((response: IAdvancedDelivery) => {
            const next = toDraft(response);
            setData(response);
            setDraft(next);
            setInitialSnapshot(JSON.stringify(next));
            setShowErrors(false);
        }, []);

        // Reload every time the tab is opened (the agency can change on the General tab), but never drop an unsaved draft.
        useEffect(() => {
            if (!isActive || isDirty) return;
            setIsLoading(true);
            UserService.getAdvancedDelivery(userId)
                .then(applyData)
                .catch(err => ErrorToast(err))
                .finally(() => setIsLoading(false));
        }, [isActive, userId, applyData]);

        const errors = useMemo(() => {
            const result: Record<string, string> = {};
            if (!draft) return result;
            REQUIRED_FIELDS.forEach(([key, message]) => {
                if (!draft[key]?.trim()) result[key] = message;
            });
            return result;
        }, [draft]);

        useImperativeHandle(ref, () => ({
            validate: () => {
                setShowErrors(true);
                return Object.keys(errors).length === 0;
            },
            save: async () => {
                if (!draft) return;
                const response = await UserService.saveAdvancedDelivery(userId, draft);
                applyData(response);
            },
        }));

        const setField = (field: keyof Draft, value: string | boolean) =>
            setDraft(prev => (prev ? { ...prev, [field]: value } : prev));

        const text = (label: string, field: keyof IAdvancedDeliveryAddress, md = 6, required = true) => (
            <Form.Group as={Col} md={md} className="mb-3">
                <Form.Label>
                    {label}
                    {required && <span className="text-danger"> *</span>}
                </Form.Label>
                <Form.Control
                    value={draft?.[field] ?? ''}
                    onChange={event => setField(field, event.target.value)}
                    isInvalid={showErrors && !!errors[field]}
                />
                <Form.Control.Feedback type="invalid">{errors[field]}</Form.Control.Feedback>
            </Form.Group>
        );

        if ((isLoading && !data) || !draft || !data) {
            return (
                <div className="text-center py-10">
                    <Spinner animation="border" />
                </div>
            );
        }

        // Live version of the server rule: an agency influencer ships to the agency unless "ship to influencer" is on.
        const usesAgency = data.isAgencyManaged && !draft.useOwnAddress;

        return (
            <>
                <Card className="mb-4">
                    <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        {data.isAgencyManaged && data.agency ? (
                            <>
                                <div>
                                    <div className="fw-semibold mb-1">
                                        {usesAgency
                                            ? `Packages are shipped to the agency (${data.agency.displayName})`
                                            : 'Packages are shipped to the influencer'}
                                    </div>
                                    <div className="fs-8 text-muted">
                                        This influencer is in an agency. Ship to influencer uses the influencer&apos;s
                                        delivery data from their profile. Invoice data stays unchanged.
                                    </div>
                                </div>
                                <ToggleChip
                                    label="Ship to influencer"
                                    checked={draft.useOwnAddress}
                                    onChange={event => setField('useOwnAddress', event.target.checked)}
                                />
                            </>
                        ) : (
                            <div>
                                <div className="fw-semibold mb-1">Packages are shipped to the influencer</div>
                                <div className="fs-8 text-muted">
                                    This influencer is not in an agency, so the address below is used.
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {data.isAgencyManaged && data.agency && (
                    <Card className="mb-4">
                        <Card.Body>
                            {sectionTitle(`Agency address (${data.agency.displayName})`, usesAgency ? usedBadge : null)}
                            <ReadOnlyAddress address={data.agency.delivery} />
                        </Card.Body>
                    </Card>
                )}

                <Card className="mb-4">
                    <Card.Body>
                        {sectionTitle(
                            data.isAgencyManaged ? "Influencer's own address" : 'Delivery address',
                            usesAgency ? null : usedBadge
                        )}
                        <Row>
                            {text('First name', 'firstName')}
                            {text('Last name', 'lastName')}
                            {text('Company', 'company', 12, false)}
                            {text('Street', 'street', 9)}
                            {text('Street number', 'streetNumber', 3)}
                            {text('Address line 2', 'addressLine2', 12, false)}
                            {text('Postal code', 'zip', 4)}
                            {text('City', 'city', 4)}
                            {text('State', 'state', 4, false)}
                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label>
                                    Country<span className="text-danger"> *</span>
                                </Form.Label>
                                <SingleSelectDropdown
                                    placeholder="Select country"
                                    withImage
                                    clearable={false}
                                    options={(country ?? []).map((code: string) => ({
                                        value: code,
                                        label: getCountryName(code),
                                        image: `/flags/4x3/${code.toLowerCase()}.svg`,
                                    }))}
                                    selected={draft.country ?? ''}
                                    onChange={value => setField('country', value)}
                                />
                                {showErrors && errors.country && (
                                    <div className="text-danger fs-8 mt-1">{errors.country}</div>
                                )}
                            </Form.Group>
                            {text('Phone number', 'phoneNumber')}
                            <Form.Group as={Col} md={12} className="mb-3">
                                <Form.Label>Delivery instructions</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={draft.deliveryInstructions ?? ''}
                                    onChange={event => setField('deliveryInstructions', event.target.value)}
                                />
                            </Form.Group>
                        </Row>
                    </Card.Body>
                </Card>

                {!data.isUsedComplete && !isDirty && (
                    <Card className="mb-4 border-warning">
                        <Card.Body className="py-3 text-warning">
                            The address used for shipping is incomplete. The influencer cannot accept offers that
                            include a package until it is filled in.
                        </Card.Body>
                    </Card>
                )}
            </>
        );
    }
);
